// @ts-nocheck
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type NotificationType = 'all' | 'reaction' | 'comment' | 'mention' | 'follow' | 'badge' | 'team_goal';

export interface NotificationActor {
  id: string;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  avatar_url: string | null;
}

export interface WallNotification {
  id: string;
  type: string;
  actor_id: string;
  recipient_id: string;
  post_id: string;
  comment_id: string | null;
  is_read: boolean;
  created_at: string;
  actor: NotificationActor | null;
}

interface NotificationsPage {
  notifications: WallNotification[];
  nextCursor: number | null;
}

const PAGE_SIZE = 20;

async function fetchNotificationsPage(
  userId: string,
  filter: NotificationType,
  pageParam: number
): Promise<NotificationsPage> {
  let query = supabase
    .from('wall_notifications')
    .select(`
      id,
      type,
      actor_id,
      recipient_id,
      post_id,
      comment_id,
      is_read,
      created_at,
      profiles!wall_notifications_actor_id_fkey (
        id,
        first_name,
        last_name,
        username,
        avatar_url
      )
    `)
    .eq('recipient_id', userId)
    .order('created_at', { ascending: false })
    .range(pageParam, pageParam + PAGE_SIZE - 1);

  // Apply type filter
  if (filter !== 'all') {
    query = query.eq('type', filter);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }

  const notifications: WallNotification[] = (data || []).map((item: any) => ({
    ...item,
    actor: item.profiles as NotificationActor | null,
  }));

  return {
    notifications,
    nextCursor: notifications.length === PAGE_SIZE ? pageParam + PAGE_SIZE : null,
  };
}

export function useNotifications(filter: NotificationType = 'all') {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ['wall-notifications', filter, user?.id],
    queryFn: ({ pageParam = 0 }) => 
      fetchNotificationsPage(user!.id, filter, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 1, // 1 minute
  });

  const allNotifications = query.data?.pages.flatMap(page => page.notifications) || [];

  const invalidateNotifications = () => {
    queryClient.invalidateQueries({ queryKey: ['wall-notifications'] });
    queryClient.invalidateQueries({ queryKey: ['unread-notifications-count'] });
  };

  return {
    notifications: allNotifications,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    invalidateNotifications,
    error: query.error,
  };
}

export function useUnreadNotificationsCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['unread-notifications-count', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from('wall_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error fetching unread count:', error);
        return 0;
      }

      return count || 0;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // Refresh every minute
  });
}
